// Two-click player for videos from YouTube and Vimeo (data protection): the lightbox first shows the locally stored
// preview image with a notice, the iframe of the provider only exists after the click. A visitor can remember the
// decision per provider (localStorage, no cookie); later videos then start right away.
//
// Markup: <a href="video url" data-video-gallery="name" data-video-consent='{"provider","embed","title","load","hint",
// "remember","poster"}' data-caption>, see components/cards/videolink.html.twig. Without JavaScript the link simply
// opens the video at the provider. The lightbox is GLightbox with the options of the image lightbox (js/lightbox.js),
// loaded on the first click (same chunk as the image lightbox, init/initLightbox.js).
import {loadLightbox} from './initLightbox';

const STORAGE_KEY = 'video-consent';

function remembered(provider) {
    try {
        return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}')[provider] === true;
    } catch (e) {
        return false;
    }
}

function remember(provider) {
    try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
        stored[provider] = true;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch (e) {
        // storage blocked: the question simply comes again next time
    }
}

function escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value ?? '';
    return element.innerHTML.replace(/"/g, '&quot;');
}

function slideMarkup(data) {
    const poster = data.poster ? ` style="--video-poster: url(&quot;${escapeHtml(data.poster)}&quot;)"` : '';
    const json = escapeHtml(JSON.stringify({provider: data.provider, embed: data.embed, title: data.title}));
    return `<div class="video-consent" data-state="ask" data-video="${json}">
        <div class="video-consent__frame"${poster}>
            <div class="video-consent__box">
                <button type="button" class="btn btn-primary video-consent__load">${escapeHtml(data.load)}</button>
                <p class="video-consent__hint">${escapeHtml(data.hint)}</p>
                <label class="video-consent__remember"><input type="checkbox" class="form-check-input"> ${escapeHtml(data.remember)}</label>
            </div>
        </div>
    </div>`;
}

// autoplay: the visitor just clicked the button; a remembered decision at page load must not start a video by itself
function start(root, autoplay = true) {
    if (root.dataset.state === 'playing') {
        return;
    }
    const video = JSON.parse(root.dataset.video);
    const frame = root.querySelector('.video-consent__frame');
    const template = root.querySelector(':scope > template');
    root._ask = frame.innerHTML;

    if (video.embed) {
        // YouTube/Vimeo: player address (the lightbox and the button of a page both want to play right away)
        const iframe = document.createElement('iframe');
        iframe.src = autoplay ? video.embed : video.embed.replace(/([?&])autoplay=1&?/, '$1').replace(/[?&]$/, '');
        iframe.title = video.title || '';
        iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
        frame.replaceChildren(iframe);
    } else if (template) {
        // any other embed code of an editor, waiting unevaluated in the <template>
        frame.replaceChildren(template.content.cloneNode(true));
    } else {
        return;
    }
    root.dataset.state = 'playing';
}

// leaving a slide stops its video: back to the preview
function reset(root) {
    if (root.dataset.state !== 'playing') {
        return;
    }
    root.querySelector('.video-consent__frame').innerHTML = root._ask;
    root.dataset.state = 'ask';
}

// the slide in view plays at once for a remembered provider, every other one goes back to its preview
function sync(container) {
    container.querySelectorAll('.gslide').forEach((slide) => {
        const root = slide.querySelector('.video-consent');
        if (!root) {
            return;
        }
        if (slide.classList.contains('current')) {
            if (remembered(JSON.parse(root.dataset.video).provider)) {
                start(root);
            }
        } else {
            reset(root);
        }
    });
}

async function open(link) {
    const {GLightbox, lightboxOptions} = await loadLightbox();
    const group = [...document.querySelectorAll('a[data-video-consent]')]
        .filter((candidate) => candidate.dataset.videoGallery === link.dataset.videoGallery);

    const lightbox = GLightbox({
        ...lightboxOptions,
        elements: group.map((candidate, index) => {
            const data = JSON.parse(candidate.dataset.videoConsent);
            const caption = candidate.dataset.caption || data.title || '';
            return {
                content: slideMarkup(data),
                description: group.length > 1 ? `${index + 1} / ${group.length}<br>${caption}` : caption,
                width: 'auto',
                height: 'auto',
                zoomable: false,
                draggable: false,
            };
        }),
        startAt: Math.max(0, group.indexOf(link)),
    });
    lightbox.on('slide_changed', () => sync(document.querySelector('.glightbox-container') ?? document));
    lightbox.on('close', () => window.setTimeout(() => lightbox.destroy(), 400));
    lightbox.open();
}

export default function initVideoConsent() {
    // players in the page (video-consent.html.twig) of a provider the visitor agreed to: no question
    document.querySelectorAll('.video-consent--inline').forEach((root) => {
        if (remembered(JSON.parse(root.dataset.video).provider)) {
            start(root, false);
        }
    });

    document.addEventListener('click', (event) => {
        const button = event.target.closest('.video-consent__load');
        if (button) {
            const root = button.closest('.video-consent');
            if (root.querySelector('input[type="checkbox"]')?.checked) {
                remember(JSON.parse(root.dataset.video).provider);
            }
            start(root);
            return;
        }

        const link = event.target.closest('a[data-video-consent]');
        if (link && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
            event.preventDefault();
            open(link);
        }
    });
}
